// The part of a freeze frame that is the same whoever takes it: the burn-in,
// the thumbnail, the JPEG encode and the EXIF fields. viewer.js uses it for
// the Freeze button and a POI's "Save frame"; the web app's Saved Frames page
// uses it to save frames for POIs in bulk. One home, so the two cannot drift.
import { tagJpegBlob } from "./viewer.freeze.exif.js";
import { buildCaptionLines } from "./viewer.freeze.metadata.js";

export const EXIF_SOFTWARE = "Hawk Atlas";

export const fmtLatLon = (p) => (p ? `${p.lat.toFixed(6)},${p.lon.toFixed(6)}` : "unknown");

// Draw the translucent caption box + monospace lines, bottom-left.
export function drawCaptionLines(ctx, w, h, lines) {
  const fs = Math.max(16, Math.round(w / 85));
  const lh = Math.round(fs * 1.65), pad = Math.round(fs * 0.8);
  const boxH = lines.length * lh + pad * 2;
  const boxW = Math.round(w * 0.28);
  ctx.fillStyle = "rgba(0,0,0,0.65)";
  ctx.fillRect(0, h - boxH, boxW, boxH);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${fs}px monospace`;
  lines.forEach((ln, i) => ctx.fillText(ln, pad, h - boxH + pad + (i + 1) * lh - 4));
}

// 320x180 letterboxed thumbnail derived from a full-res capture canvas.
export function buildThumbCanvas(full, doc = document) {
  const thumb = doc.createElement("canvas");
  thumb.width = 320; thumb.height = 180;
  const tctx = thumb.getContext("2d");
  tctx.fillStyle = "#000";
  tctx.fillRect(0, 0, 320, 180);
  const fitW = 320, fitH = Math.round(320 * (full.height / full.width));
  const offY = Math.floor((180 - fitH) / 2);
  tctx.drawImage(full, 0, offY, fitW, fitH);
  return thumb;
}

// Promise-wrapped canvas.toBlob for JPEG.
export function canvasToJpegBlob(canvas, quality) {
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
}

// The full-res JPEG a freeze frame ships as: encoded once, then tagged with
// EXIF (timestamp + geotag) so the download and the upload are the same
// bytes. A tagging failure must never lose the capture: fall back to the bare
// JPEG and log.
export async function encodeTaggedJpeg(canvas, exifFields) {
  const bare = await canvasToJpegBlob(canvas, 0.92);
  if (!bare) return null;
  try {
    return await tagJpegBlob(bare, exifFields);
  } catch (e) {
    console.warn("Freeze: EXIF tagging failed, saving untagged JPEG", e);
    return bare;
  }
}

/** EXIF fields for a frame taken from the video (the Freeze button, a POI's
 *  "Save frame", a bulk POI capture). `position` is resolveFramePosition's
 *  result; `aircraft` is the aircraft position at that moment, or null. */
export function buildVideoFrameExif({
  utcS, tz, position, flightId, poiName, videoTimeS, aircraft,
}) {
  return {
    utcS, tz,
    lat: position?.lat ?? null, lon: position?.lon ?? null,
    description: flightId + (poiName ? ` - POI: ${poiName}` : ""),
    software: EXIF_SOFTWARE,
    userComment: `flight=${flightId} video_t=${videoTimeS.toFixed(3)} ` +
      `geotag=${position?.source ?? "none"} aircraft=${fmtLatLon(aircraft)}`,
  };
}

/** Burn the caption into `canvas` (already holding the picture) and return
 *  the tagged full-res JPEG and the 320x180 thumbnail. Null when the full-res
 *  image cannot be encoded; `thumbBlob` alone may be null. */
export async function renderFreezeFrame(canvas, fields, doc = document) {
  drawCaptionLines(canvas.getContext("2d"), canvas.width, canvas.height, buildCaptionLines({
    utcS: fields.utcS, tz: fields.tz, position: fields.position, flightId: fields.flightId,
  }));
  const fullBlob = await encodeTaggedJpeg(canvas, buildVideoFrameExif(fields));
  if (!fullBlob) return null;
  // A thumbnail failure (e.g. getContext("2d") returning null under memory
  // pressure) must never lose the capture: fall back to the full-res frame
  // alone. Callers already handle a null thumbBlob.
  try {
    const thumbBlob = await canvasToJpegBlob(buildThumbCanvas(canvas, doc), 0.7);
    return { fullBlob, thumbBlob };
  } catch (e) {
    console.warn("Freeze: thumbnail failed, saving the full-res frame only", e);
    return { fullBlob, thumbBlob: null };
  }
}
